import TileGridContainer from 'features/tiles/tileGridContainer';
import { FC, useMemo, useState } from 'react';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import Textbox from 'common/components/inputs/textbox';
import Textarea from 'common/components/inputs/textarea';
import Button from 'common/components/buttons/button';
import DropDownButtonLegacy from 'common/components/drop-downs/legacy/dropDownButtonLegacy';
import SimpleButtonContainerContent from 'common/components/drop-downs/legacy/simpleDropDownButtonContainerContent';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import {
  useCreateWebhookMutation,
  useValidateWebhookEndpointMutation
} from 'app/apis/commandApi';
import { RootState } from 'app/store';
import { useSelector, useDispatch } from 'react-redux';
import { onSuccess } from 'features/toast/toast.slice';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import NavLinkButton from 'common/components/anchors/navlink-button';
import { useNavigate } from 'react-router-dom';
import { ValidationProblemDetail } from 'common/types/validation-problem-detail';
import {
  WebhookTypes,
  getWebhookTypeFriendlyName
} from 'features/webhooks/webhook-types';
import { useAuth } from 'features/auth/hooks/useAuth';
import Toggle, { ToggleSize } from 'common/components/inputs/toggle';
import { useGetWebhookDataFieldsQuery } from 'app/apis/queryApi';
import { WebhookDataField } from 'features/webhooks/webhooks.models';

const webhookNameMinLength = 1;
const webhookNameMaxLength = 50;
const validateWebhookName = (webhookName: string): string[] => {
  if (webhookName.length < webhookNameMinLength) {
    return [`Webhook Name must be at least ${webhookNameMinLength} character`];
  }

  if (webhookName.length > webhookNameMaxLength) {
    return [
      `Webhook Name must be less than ${webhookNameMaxLength} characters`
    ];
  }

  return [];
};

const webhookDescriptionMaxLength = 120;
const validateWebhookDescription = (webhookDescription: string): string[] => {
  if (webhookDescription.length > webhookDescriptionMaxLength) {
    return [
      `Webhook Description must be at less than ${webhookDescriptionMaxLength} characters`
    ];
  }

  return [];
};

const webhookUrlMinLength = 1;
const validateWebhookUrl = (webhookUrl: string): string[] => {
  if (webhookUrl.length < webhookUrlMinLength) {
    return [`Webhook Url must be at least ${webhookUrlMinLength} character`];
  }

  return [];
};

export const SettingsWebhooksCreatePage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { isSuperUser } = useAuth();
  const isPocModeActive = useSelector(
    (state: RootState) => state.pocToggle.isPocModeActive
  );

  const [webhookName, onWebhookNameChanged] = useState('');
  const [webhookNameErrors, setWebhookNameErrors] = useState(
    new Array<string>()
  );

  const [webhookDescription, onWebhookDescriptionChanged] = useState('');
  const [webhookDescriptionErrors, setWebhookDescriptionErrors] = useState(
    new Array<string>()
  );

  const [selectedWebhookType, onSelectedWebhookTypeChanged] = useState(
    WebhookTypes.CONVERSION_WEB
  );
  const allWebhookTypes = useMemo(
    () =>
      Object.values(WebhookTypes).map<DropDownItemType>((webhookType) => {
        return {
          key: webhookType,
          label: getWebhookTypeFriendlyName(webhookType),
          isActive: selectedWebhookType === webhookType
        };
      }),
    [selectedWebhookType]
  );

  const [webhookUrl, onWebhookUrlChanged] = useState('');
  const [webhookUrlErrors, setWebhookUrlErrors] = useState(new Array<string>());
  const [isWebhookValidating, onIsWebhookValidatingChanged] = useState(false);
  const [isWebhookUrlValid, onIsWebhookUrlValidChanged] = useState(false);
  const [validateWebhookEndpoint] = useValidateWebhookEndpointMutation();

  const onValidateWebhookEndpoint = () => {
    onIsWebhookValidatingChanged(true);

    void validateWebhookEndpoint({
      siteId,
      url: webhookUrl
    })
      .unwrap()
      .then(() => {
        onIsWebhookUrlValidChanged(true);
        onIsWebhookValidatingChanged(false);
        setWebhookUrlErrors([]);

        dispatch(
          onSuccess({
            title: 'URL is Valid',
            description:
              'The URL you have entered is valid. If you change the URL then you will need to test it again.'
          })
        );
      })
      .catch(() => {
        onIsWebhookUrlValidChanged(false);
        onIsWebhookValidatingChanged(false);

        const errors = ['URL is not valid'];
        setWebhookUrlErrors(errors);
      });
  };

  const [isInternalWebhook, onIsInternalWebhookChanged] = useState(false);
  const [useCustomDataSchema, onUseCustomDataSchemaChanged] = useState(false);
  const [selectedCustomDataFields, onSelectedCustomDataFieldsChanged] =
    useState<WebhookDataField[]>(new Array<WebhookDataField>());

  const isFormValid =
    webhookNameErrors.length < 1 &&
    webhookUrlErrors.length < 1 &&
    isWebhookUrlValid;

  const {
    data: webhookDataFieldsResult,
    isLoading: isFetchingWebhookDataFields
  } = useGetWebhookDataFieldsQuery({
    siteId,
    webhookType: selectedWebhookType
  });

  const webhookDataFields = useMemo(() => {
    if (!useCustomDataSchema) {
      return [];
    }

    if (webhookDataFieldsResult && !isFetchingWebhookDataFields) {
      return webhookDataFieldsResult.webhookDataFields.map<WebhookDataField>(
        (df) => {
          return {
            ...df,
            enabled: selectedCustomDataFields.some(
              (selectedCustomDataField) =>
                selectedCustomDataField.name === df.name
            )
          };
        }
      );
    }

    return [];
  }, [
    webhookDataFieldsResult,
    isFetchingWebhookDataFields,
    selectedCustomDataFields,
    useCustomDataSchema
  ]);

  const [createWebhook] = useCreateWebhookMutation();
  const [isSaving, onIsSavingChanged] = useState(false);

  const onCreateWebhook = () => {
    onIsSavingChanged(true);

    void createWebhook({
      siteId,
      webhookName,
      webhookDescription,
      webhookType: selectedWebhookType,
      webhookUrl,
      isInternalWebhook,
      useCustomDataSchema,
      dataFields: webhookDataFields
    })
      .unwrap()
      .then(() => {
        onIsSavingChanged(false);

        dispatch(
          onSuccess({
            title: 'Webhook Created',
            description:
              'The webhook has successfully been created! Any new events will now be posted to it.'
          })
        );
        navigate('/settings/webhooks');
      })
      .catch(({ data }: { data: ValidationProblemDetail }) => {
        onIsSavingChanged(false);

        const propertyName = Object.keys(data.Errors)[0];
        const errorDescription = data.Errors[propertyName][0];

        switch (propertyName) {
          case 'WebhookName':
            setWebhookNameErrors([errorDescription]);
            break;
          case 'WebhookUrl':
            setWebhookUrlErrors([errorDescription]);
            break;
          default:
            break;
        }
      });
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <h2 className="text-primary-700 text-l leading-7 pb-2 mb-3 mt-2 border-b border-neutral-200">
            New Webhook
          </h2>
          <p className="text-sm">
            When creating a new Webhook you must have a unique name, a Webhook
            Type and validated Webhook URL
          </p>
          <div className="lg:w-144 mt-4">
            <label htmlFor="webhook-name" className="block mb-1 text-sm">
              Webhook Name
            </label>
            <Textbox
              identifier="webhook-name"
              errors={webhookNameErrors}
              maxCharacterLimit={webhookNameMaxLength}
              value={webhookName}
              onChange={(value) => {
                const errors = validateWebhookName(value);
                setWebhookNameErrors(errors);
                onWebhookNameChanged(value);

                onIsWebhookUrlValidChanged(false);
              }}
            />
          </div>

          <div className="lg:w-144 mt-2">
            <label htmlFor="webhook-description" className="block mb-1 text-sm">
              Webhook Description
            </label>
            <Textarea
              identifier="webhook-description"
              errors={webhookDescriptionErrors}
              maxCharacterLimit={webhookDescriptionMaxLength}
              value={webhookDescription}
              onChange={(value) => {
                const errors = validateWebhookDescription(value);
                setWebhookDescriptionErrors(errors);
                onWebhookDescriptionChanged(value);
              }}
            />
          </div>

          <div className="lg:w-144 mt-2">
            <label className="block mb-1 text-sm">Webhook Type</label>
            <DropDownButtonLegacy
              dropDownItems={allWebhookTypes}
              selectDropDownItem={(webhookType) => {
                onSelectedWebhookTypeChanged(webhookType as WebhookTypes);
                onSelectedCustomDataFieldsChanged([]);
              }}
              dropDirection={'left'}
            >
              <SimpleButtonContainerContent
                dropdownLabel={getWebhookTypeFriendlyName(selectedWebhookType)}
                additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
              />
            </DropDownButtonLegacy>
          </div>

          {isPocModeActive ? (
            <div className="mt-5">
              <label htmlFor="use-custom-data-field" className="block text-sm">
                Use Custom Data Fields
              </label>
              <Toggle
                identifier="use-custom-data-field"
                label=""
                size={ToggleSize.Large}
                checked={useCustomDataSchema}
                onChange={() =>
                  onUseCustomDataSchemaChanged(!useCustomDataSchema)
                }
              />
            </div>
          ) : (
            <></>
          )}

          {isPocModeActive && useCustomDataSchema ? (
            <div className="mt-3">
              <p className="text-sm mb-3">
                Please select the fields you would like to include:
              </p>
              {webhookDataFields?.map((df) => (
                <div key={df.displayName} className="mb-3 flex items-center">
                  <Toggle
                    identifier={`data-field-${df.displayName}`}
                    label=""
                    size={ToggleSize.Large}
                    checked={df.enabled}
                    onChange={() => {
                      const newWebhookDataFields = df.enabled
                        ? selectedCustomDataFields.filter(
                            (selectedDf) =>
                              selectedDf.displayName !== df.displayName
                          )
                        : [...selectedCustomDataFields, df];
                      onSelectedCustomDataFieldsChanged(newWebhookDataFields);
                    }}
                  />
                  <label
                    htmlFor={`data-field-${df.displayName}`}
                    className="text-sm"
                  >
                    {df.displayName}
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <></>
          )}

          <div className="lg:w-144 mt-5">
            <label htmlFor="webhook-url" className="block mb-1 text-sm">
              Webhook URL
            </label>
            <div className="flex">
              <div className="grow">
                <Textbox
                  identifier="webhook-url"
                  errors={webhookUrlErrors}
                  value={webhookUrl}
                  onChange={(value) => {
                    const errors = validateWebhookUrl(value);
                    setWebhookUrlErrors(errors);
                    onWebhookUrlChanged(value);
                  }}
                />
              </div>

              <div className="grow-0">
                <Button
                  classOverrides="ml-3 p-2"
                  isDisabled={webhookUrl.length < 1}
                  isLoading={isWebhookValidating}
                  theme={ButtonTheme.SECONDARY}
                  onClick={onValidateWebhookEndpoint}
                  identifier="test-webhook"
                >
                  <span>Validate Webhook</span>
                </Button>
              </div>
            </div>
          </div>

          {isSuperUser ? (
            <div className="mt-5">
              <label htmlFor="is-internal-webhook" className="block text-sm">
                Create as an Internal Webhook
              </label>
              <Toggle
                identifier="is-internal-webhook"
                label=""
                size={ToggleSize.Large}
                checked={isInternalWebhook}
                onChange={() => onIsInternalWebhookChanged(!isInternalWebhook)}
              />
            </div>
          ) : (
            <></>
          )}

          <div className="mt-6 flex items-center">
            <Button
              isDisabled={!isFormValid}
              theme={ButtonTheme.PRIMARY}
              onClick={onCreateWebhook}
              isLoading={isSaving}
              identifier="create-webhook"
            >
              <span>Create Webhook</span>
            </Button>

            <div className="ml-4">
              <NavLinkButton
                theme={ButtonTheme.CANCEL}
                identifier="save-webhook"
                to="/settings/webhooks"
                classOverrides="px-4 py-2.5 block"
              >
                <span>Cancel</span>
              </NavLinkButton>
            </div>
          </div>
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
